import { computed, ref } from 'vue'
import { contentProvider } from '#root/renderer/contentProvider'
import { computedAsync, useMemoize } from '@vueuse/core'
import { createInjectionState } from '@vueuse/shared'
import { useCartSession } from '#root/lib/useCartSession'
import type { FindVariantsResponse } from '#root/types/variants'

const [useProvideVariantsStore, useVariantsStore] = createInjectionState(() => {
  const { testMode } = useCartSession()
  const isFetching = ref(false)
  const fetchTime = ref(new Date().getTime())

  const findVariants = useMemoize(
    async (cacheKey: number): Promise<FindVariantsResponse> => {
      void cacheKey // used as memo cache key by useMemoize
      const raw = await contentProvider.findVariants(true, testMode.value)
      return raw as FindVariantsResponse
    }
  )

  const data = computedAsync(
    async () => findVariants(fetchTime.value),
    null as FindVariantsResponse | null,
    {
      evaluating: isFetching,
      lazy: true
    }
  )

  const indexes = computed(() => data.value?.indexes)
  const uniqueIndexes = computed(() => data.value?.unique_indexes)
  const items = computed(() => data.value?.data ?? null)

  function refetchData(): void {
    findVariants.clear()
    fetchTime.value = new Date().getTime()
  }

  return {
    fetchTime,
    findVariants,
    indexes,
    isFetching,
    items,
    refetchData,
    uniqueIndexes
  }
})

function useVariantsStoreOrThrow() {
  const variantsStore = useVariantsStore()
  if (variantsStore == null)
    throw new Error(
      'Please call `useProvideVariantsStore` on the appropriate parent component'
    )
  return variantsStore
}

export { useProvideVariantsStore, useVariantsStoreOrThrow }
